import React, { useState, useEffect } from 'react';
import {
  addProduct,
  uploadProductImages,
  uploadProductImage,
  getAllCompanies,
  getCustomCompaniesLocal,
  saveCustomCompanyLocal,
  getCompanyLogoMapLocal,
  getCompanyLogoLocal,
} from '../api/productApi';
import { getAllCategories } from '../api/categoryApi';
import { useNavigate } from 'react-router-dom';
import AdminNav from './AdminNav';
import ManageCompaniesModal from './ManageCompaniesModal';
import './AdminDashboard.css';
import { resolveImageUrl } from '../utils/image';
import { isValidYouTubeUrl, getYouTubeVideoId } from '../utils/youtube';

export default function AddProductPage() {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [price, setPrice] = useState<string>('');
  const [qty, setQty] = useState<string>('');
  const [videoUrl, setVideoUrl] = useState('');
  const [displayOrder, setDisplayOrder] = useState<string>('');
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [urlInput, setUrlInput] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string>('');
  const [companyLogos, setCompanyLogos] = useState<Record<string, string>>({});
  const [uploadingCompanyLogo, setUploadingCompanyLogo] = useState<boolean>(false);
  const [isAddingNewCompany, setIsAddingNewCompany] = useState<boolean>(false);
  const [newCompanyInput, setNewCompanyInput] = useState<string>('');
  const [companies, setCompanies] = useState<string[]>([]);
  const [showManageCompanies, setShowManageCompanies] = useState<boolean>(false);
  const [isMostSale, setIsMostSale] = useState<boolean>(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategoriesAndCompanies = async () => {
      try {
        const response = await getAllCategories();
        const data = Array.isArray(response) ? response : response.value;
        const list = data || [];
        setCategories(list);
        if (list.length > 0) {
          setCategoryId(list[0].categoryID);
        }
      } catch {
        setCategories([]);
      }

      try {
        const apiCompanies = await getAllCompanies();
        const localCompanies = getCustomCompaniesLocal();
        const logosMap = { ...getCompanyLogoMapLocal() };
        apiCompanies.forEach(c => {
          if (c.companyName && c.companyLogoUrl) {
            logosMap[c.companyName.trim().toLowerCase()] = c.companyLogoUrl;
          }
        });
        setCompanyLogos(logosMap);
        const merged = Array.from(
          new Set([
            ...apiCompanies.map(c => c.companyName),
            ...localCompanies,
          ].filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
        setCompanies(merged);
      } catch {
        setCompanies(getCustomCompaniesLocal());
        setCompanyLogos(getCompanyLogoMapLocal());
      }
    };
    fetchCategoriesAndCompanies();
  }, []);

  const handleSelectCompany = (selected: string) => {
    setCompanyName(selected);
    if (selected) {
      const existingLogo = companyLogos[selected.trim().toLowerCase()] || getCompanyLogoLocal(selected);
      setCompanyLogoUrl(existingLogo || '');
    } else {
      setCompanyLogoUrl('');
    }
  };

  const handleAddCustomCompany = () => {
    const trimmed = newCompanyInput.trim();
    if (!trimmed) return;
    const updated = saveCustomCompanyLocal(trimmed, companyLogoUrl.trim() || undefined);
    const merged = Array.from(new Set([...companies, ...updated])).sort((a, b) => a.localeCompare(b));
    setCompanies(merged);
    if (companyLogoUrl.trim()) {
      setCompanyLogos(prev => ({ ...prev, [trimmed.toLowerCase()]: companyLogoUrl.trim() }));
    }
    setCompanyName(trimmed);
    setNewCompanyInput('');
    setIsAddingNewCompany(false);
  };

  const handleUploadCompanyLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCompanyLogo(true);
    try {
      const res = await uploadProductImage(file);
      if (res?.path) {
        setCompanyLogoUrl(res.path);
      }
    } catch {
      setError('Failed to upload company logo image. Please try again or paste a logo URL.');
    } finally {
      setUploadingCompanyLogo(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!name || !desc || (imageUrls.length === 0 && files.length === 0) || !categoryId) {
      setError('Please fill in all required fields and provide at least one product image.');
      setLoading(false);
      return;
    }

    const parsedPrice = price === '' ? null : Number(price);
    const parsedQty = qty === '' ? null : Number(qty);
    if (parsedPrice == null || isNaN(parsedPrice) || parsedQty == null || isNaN(parsedQty)) {
      setError('Please enter a valid price and available quantity.');
      setLoading(false);
      return;
    }

    const finalCompanyName = (isAddingNewCompany && newCompanyInput.trim())
      ? newCompanyInput.trim()
      : companyName.trim();
    const finalCompanyLogoUrl = companyLogoUrl.trim();

    if (finalCompanyName) {
      saveCustomCompanyLocal(finalCompanyName, finalCompanyLogoUrl || undefined);
    }

    try {
      let finalImageUrls = [...imageUrls];
      if (files.length > 0) {
        const uploaded = await uploadProductImages(files);
        if (uploaded?.paths && Array.isArray(uploaded.paths)) {
          finalImageUrls = finalImageUrls.concat(uploaded.paths);
        }
      }

      const result = await addProduct({
        Name: name,
        Description: desc,
        Price: parsedPrice,
        QuantityAvailable: parsedQty,
        ImageUrls: finalImageUrls,
        CategoryId: Number(categoryId),
        IsMostSaleProduct: isMostSale,
        VideoUrl: videoUrl.trim() || undefined,
        CompanyName: finalCompanyName || undefined,
        CompanyLogoUrl: finalCompanyName && finalCompanyLogoUrl ? finalCompanyLogoUrl : undefined,
        DisplayOrder: displayOrder ? parseInt(displayOrder, 10) : undefined,
      });

      if (result.value?.message === "Product added successfully") {
        navigate('/admin/products');
      } else {
        setError(result.value?.message || 'Failed to add product.');
      }
    } catch {
      setError('Server error while saving product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ad-page-wrapper">
      <AdminNav activeTab="add-product" />

      <main className="ad-main-content">
        <div className="ad-form-page-container">
          {/* Header */}
          <div className="ad-form-header">
            <button className="ad-back-btn" onClick={() => navigate('/admin/products')}>
              &larr; Back to Products
            </button>
            <div className="ad-form-title-group">
              <span className="ad-modal-tag">INVENTORY MANAGEMENT</span>
              <h1 className="ad-form-main-title">Add New Fireworks Product</h1>
              <p className="ad-form-subtitle">Create a new item in your fireworks catalog with rates and gallery images</p>
            </div>
          </div>

          {/* Form Card */}
          <div className="ad-form-card">
            <form onSubmit={handleSubmit} className="ad-crud-form">
              <div className="ad-form-field">
                <label>Product Name <span className="req">*</span></label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  className="ad-input"
                  placeholder="e.g. 1000 Wala Red Garland, 7cm Sparklers Gold..."
                />
              </div>

              <div className="ad-form-row-2">
                <div className="ad-form-field">
                  <label>Category <span className="req">*</span></label>
                  <select
                    className="ad-select"
                    value={categoryId}
                    onChange={e => setCategoryId(Number(e.target.value))}
                    required
                  >
                    {categories.map((cat: any) => (
                      <option key={cat.categoryID} value={cat.categoryID}>
                        {cat.categoryName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ad-form-field">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, flexWrap: 'wrap' }}>
                    <label style={{ marginBottom: 0 }}>Company / Brand Name</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button
                        type="button"
                        className="ad-company-toggle-btn"
                        onClick={() => {
                          setIsAddingNewCompany(prev => !prev);
                          setNewCompanyInput('');
                        }}
                      >
                        {isAddingNewCompany ? 'Select List' : '+ Add New'}
                      </button>
                      <button
                        type="button"
                        className="ad-company-toggle-btn secondary"
                        onClick={() => setShowManageCompanies(true)}
                        title="Modify or remove company names & logos from list"
                      >
                        ⚙️ Modify / Remove
                      </button>
                    </div>
                  </div>

                  {!isAddingNewCompany ? (
                    <select
                      className="ad-select"
                      value={companyName}
                      onChange={e => {
                        if (e.target.value === '__ADD_NEW__') {
                          setIsAddingNewCompany(true);
                          setNewCompanyInput('');
                        } else {
                          handleSelectCompany(e.target.value);
                        }
                      }}
                    >
                      <option value="">-- Select Company / Brand --</option>
                      {companies.map((comp, idx) => (
                        <option key={idx} value={comp}>
                          {comp}
                        </option>
                      ))}
                      <option value="__ADD_NEW__">+ Type / Add New Company Name...</option>
                    </select>
                  ) : (
                    <div className="ad-company-new-row">
                      <input
                        type="text"
                        value={newCompanyInput}
                        onChange={e => setNewCompanyInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCustomCompany();
                          }
                        }}
                        className="ad-input"
                        placeholder="Type company name (e.g. Standard, Sony, Cock Brand...)"
                        autoFocus
                      />
                      <button
                        type="button"
                        className="ad-btn-add-url"
                        onClick={handleAddCustomCompany}
                      >
                        Add
                      </button>
                    </div>
                  )}

                  {(companyName || isAddingNewCompany) && (
                    <div className="ad-company-logo-box">
                      <label className="ad-company-logo-label">
                        Company Logo <span className="ad-field-optional">(Upload image or paste URL)</span>
                      </label>
                      <div className="ad-company-logo-controls">
                        <div className="ad-company-logo-preview-mini">
                          {companyLogoUrl.trim() ? (
                            <img
                              src={resolveImageUrl(companyLogoUrl)}
                              alt="Company Logo"
                              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <span>🏢</span>
                          )}
                        </div>
                        <input
                          type="text"
                          value={companyLogoUrl}
                          onChange={e => setCompanyLogoUrl(e.target.value)}
                          className="ad-input"
                          placeholder="https://example.com/company-logo.png"
                        />
                        <label className="ad-company-upload-label">
                          {uploadingCompanyLogo ? 'Uploading...' : '📤 Upload Logo'}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleUploadCompanyLogo}
                            disabled={uploadingCompanyLogo}
                            style={{ display: 'none' }}
                          />
                        </label>
                        {companyLogoUrl && (
                          <button
                            type="button"
                            className="ad-btn-action close"
                            onClick={() => setCompanyLogoUrl('')}
                            title="Clear logo"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="ad-form-field">
                <label className="ad-checkbox-label">
                  <input
                    type="checkbox"
                    checked={isMostSale}
                    onChange={e => setIsMostSale(e.target.checked)}
                  />
                  <span>Mark as <strong>Top Deal / Most Sale</strong></span>
                </label>
              </div>

              <div className="ad-form-row-3">
                <div className="ad-form-field">
                  <label>Price in Rupees (₹) <span className="req">*</span></label>
                  <input
                    type="number"
                    value={price}
                    onChange={e => setPrice(e.target.value)}
                    required
                    className="ad-input"
                    min="0"
                    step="0.01"
                    placeholder="e.g. 250"
                  />
                  <span className="ad-field-hint">MRP will show as 2× this price</span>
                </div>

                <div className="ad-form-field">
                  <label>Available Quantity (Stock) <span className="req">*</span></label>
                  <input
                    type="number"
                    value={qty}
                    onChange={e => setQty(e.target.value)}
                    required
                    className="ad-input"
                    min="0"
                    placeholder="e.g. 100"
                  />
                  <span className="ad-field-hint">Current unit stock available</span>
                </div>

                <div className="ad-form-field">
                  <label>Display Order / Rank in Category</label>
                  <input
                    type="number"
                    value={displayOrder}
                    onChange={e => setDisplayOrder(e.target.value)}
                    className="ad-input"
                    min="1"
                    placeholder="e.g. 1 (1st), 2 (2nd)..."
                  />
                  <span className="ad-field-hint">Rank 1 displays 1st for this category</span>
                </div>
              </div>

              <div className="ad-form-field">
                <label>Description <span className="req">*</span></label>
                <textarea
                  value={desc}
                  onChange={e => setDesc(e.target.value)}
                  required
                  className="ad-input textarea"
                  placeholder="Details about colors, sound level, spark effects, packaging details..."
                  rows={3}
                />
              </div>

              <div className="ad-form-field">
                <label>
                  YouTube Demonstration Video Link <span className="ad-field-optional">(Optional)</span>
                </label>
                <div className="ad-yt-input-wrap">
                  <span className="ad-yt-badge">YouTube</span>
                  <input
                    type="url"
                    value={videoUrl}
                    onChange={e => setVideoUrl(e.target.value)}
                    className="ad-input ad-input-yt"
                    placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                  />
                </div>
                <span className="ad-field-hint">
                  Paste YouTube video URL showing how this cracker works/bursts. Customers will be able to watch it live on the store.
                </span>
                {videoUrl.trim() && isValidYouTubeUrl(videoUrl) && (
                  <div className="ad-yt-preview-box">
                    <span>✓ Valid YouTube Video recognized (Video ID: {getYouTubeVideoId(videoUrl)})</span>
                  </div>
                )}
                {videoUrl.trim() && !isValidYouTubeUrl(videoUrl) && (
                  <span className="ad-field-warning">
                    ⚠️ Please enter a valid YouTube video link (e.g., https://www.youtube.com/watch?v=... or https://youtu.be/...)
                  </span>
                )}
              </div>

              <div className="ad-form-field">
                <label>Product Images <span className="req">*</span></label>
                
                {/* Upload Section */}
                <div className="ad-upload-input-wrap">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={e => setFiles(e.target.files ? Array.from(e.target.files) : [])}
                    className="ad-file-input"
                  />
                </div>
                {files.length > 0 && (
                  <span className="ad-files-selected-text">✓ {files.length} image file(s) selected for upload</span>
                )}

                <div className="ad-or-divider">OR PASTE EXTERNAL IMAGE URL</div>

                <div className="ad-url-add-row">
                  <input
                    type="text"
                    value={urlInput}
                    onChange={e => setUrlInput(e.target.value)}
                    className="ad-input"
                    placeholder="https://example.com/cracker-photo.jpg"
                  />
                  <button
                    type="button"
                    className="ad-btn-add-url"
                    onClick={() => {
                      if (urlInput.trim()) {
                        setImageUrls(prev => [...prev, urlInput.trim()]);
                        setUrlInput('');
                      }
                    }}
                  >
                    + Add URL
                  </button>
                </div>

                {imageUrls.length > 0 && (
                  <div className="ad-gallery-strip">
                    {imageUrls.map((u, idx) => (
                      <div key={idx} className="ad-gallery-item">
                        <img src={resolveImageUrl(u)} alt={`Thumbnail ${idx + 1}`} />
                        <button
                          type="button"
                          className="ad-gallery-remove-btn"
                          onClick={() => setImageUrls(prev => prev.filter((_, i) => i !== idx))}
                          title="Remove image"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {error && <div className="ad-error-alert">{error}</div>}

              <div className="ad-form-actions-row">
                <button 
                  className="ad-btn-action complete" 
                  type="submit" 
                  disabled={loading}
                >
                  {loading ? 'Adding Product...' : 'Publish Product to Store'}
                </button>
                <button 
                  className="ad-btn-action close" 
                  type="button" 
                  onClick={() => navigate('/admin/products')}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      <ManageCompaniesModal
        isOpen={showManageCompanies}
        companies={companies}
        companyLogos={companyLogos}
        onClose={() => setShowManageCompanies(false)}
        onCompaniesUpdated={(updatedList, renamed, removed) => {
          setCompanies(updatedList);
          setCompanyLogos({ ...getCompanyLogoMapLocal() });
          if (renamed) {
            if (companyName.toLowerCase() === renamed.oldName.toLowerCase()) {
              setCompanyName(renamed.newName);
              if (renamed.logoUrl !== undefined) {
                setCompanyLogoUrl(renamed.logoUrl);
              }
            }
          }
          if (removed && companyName.toLowerCase() === removed.toLowerCase()) {
            setCompanyName('');
            setCompanyLogoUrl('');
          }
        }}
      />
    </div>
  );
}